import * as React from 'react';
import FormControl from '@mui/material/FormControl';
import { useTranslation } from 'react-i18next';
import { useGetAvailableMetrics } from 'routes/Explore/components/Sentence/hooks/available';
import {
    Autocomplete,
    TextField,
    Typography,
    ToggleButtonGroup,
    ToggleButton,
} from '@mui/material';
import { capitalize } from 'helpers/helpers';
import { translateBlock } from 'i18n';
import Flex from 'ui/Flex/Flex';
import { useDebounce } from 'hooks/helpers';
import InputAdornment from '@mui/material/InputAdornment';
import { DashboardChartT } from 'business/definitions';
import { ChangeEvent, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAlertForm } from '../Contexts/AlertFormContext';
import NextStepButton from './NextStepButton';
import PreviousStepButton from './PreviousStepButton';
import { getAvailableAlertMetrics, getSymbolForElementType } from './helpers';
import { isThresholdValid } from './validators';
import { useAlertSentence } from '../Contexts/AlertSentenceContext';
import { useAlertAction } from '../Contexts/AlertActionContext';

type Props = {
    chart: DashboardChartT;
};

const AlertThresholdStep = ({ chart }: Props): JSX.Element => {
    const { alert, setAlert } = useAlertForm();
    const { action } = useAlertAction();
    const {
        availableMetrics,
        setAvailableMetrics,
        setAvailableFilters,
        setAvailableDimensions,
    } = useAlertSentence();

    const queryClient = useQueryClient();

    const [currentMetric, setCurrentMetric] = useState<AvailableMetricT | null>(
        null,
    );

    const [threshold, setThreshold] = useState<string>(
        alert.condition?.threshold.toString() ?? '0',
    );
    const [operation, setOperation] = useState<string | undefined>(
        alert.condition?.operation,
    );

    const { t } = useTranslation();

    const {
        data: metrics,
        isLoading,
        isFetching,
    } = useGetAvailableMetrics({ enabled: !availableMetrics });

    useEffect(() => {
        if (!isLoading && !isFetching && metrics) {
            const availableAlertMetrics = getAvailableAlertMetrics(
                chart.metrics,
                metrics,
            );
            availableAlertMetrics.sort((a, b) =>
                translateBlock(a.table).localeCompare(translateBlock(b.table)),
            );

            setAvailableMetrics(availableAlertMetrics);
        }
    }, [chart.metrics, isLoading, isFetching, metrics, setAvailableMetrics]);

    useEffect(() => {
        // If the metric is a fraction, we need to update the threshold to display it as 12.34%
        // and not 0.1234.
        if (
            currentMetric != null &&
            currentMetric.type === 'fraction' &&
            alert.condition?.threshold
        ) {
            setThreshold((alert.condition.threshold * 100.0).toFixed(2));
        }
    }, [currentMetric]);

    useEffect(() => {
        if (availableMetrics && availableMetrics.length > 0) {
            const defaultMetric = availableMetrics.find(
                (m) => m.field === alert.metric?.field,
            );

            if (defaultMetric) {
                setCurrentMetric(defaultMetric);
            }
        }
    }, [alert.metric?.field, availableMetrics]);

    const isDisabled =
        !currentMetric || !isThresholdValid(threshold) || !operation;

    const updateThreshold = useDebounce((newThreshold: string) => {
        let thresholdValue = parseFloat(newThreshold.replace(',', '.'));
        if (currentMetric != null && currentMetric.type === 'fraction') {
            thresholdValue /= 100.0;
        }
        setAlert((prevState) => ({
            ...prevState,
            condition: {
                operation: prevState?.condition?.operation ?? '',
                threshold: thresholdValue,
            },
        }));
    }, 500);

    const updateOperation = useDebounce((newOperation: string) => {
        if (newOperation) {
            setAlert((prevState) => ({
                ...prevState,
                condition: {
                    threshold: prevState?.condition?.threshold ?? 0,
                    operation: newOperation,
                },
            }));
        }
    }, 500);

    const onMetricChange = (newMetric: AvailableMetricT | null) => {
        if (newMetric) {
            setAlert((prevState) => ({
                ...prevState,
                metric: {
                    field: newMetric.field,
                    aggregation: 'sum',
                },
            }));
            setCurrentMetric(newMetric);
        }
        setAvailableFilters(null);
        setAvailableDimensions(null);
        queryClient.invalidateQueries({
            queryKey: ['get_filters'],
        });
        queryClient.invalidateQueries({
            queryKey: ['get_dimensions'],
        });
    };

    const onThresholdChange = (e: ChangeEvent<HTMLInputElement>) => {
        setThreshold(e.target.value);
        updateThreshold(e.target.value);
    };

    const onOperationChange = (value: string) => {
        setOperation(value);
        updateOperation(value);
    };

    return (
        <FormControl sx={{ width: '100%' }}>
            <Typography variant="caption">
                {t('alert.create.sentence.0')}
            </Typography>
            <Autocomplete
                value={currentMetric}
                options={availableMetrics ?? []}
                disabled={isLoading || isFetching}
                loading={isLoading || isFetching}
                onChange={(e, value) => onMetricChange(value)}
                groupBy={(option) => capitalize(translateBlock(option.table))}
                getOptionLabel={(option) =>
                    capitalize(translateBlock(option.field))
                }
                noOptionsText={t('alert.create.steps.1.noMetrics')}
                sx={{ flex: 1, mt: 1 }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        size="small"
                        label={
                            isLoading || isFetching
                                ? t('common.loading')
                                : t('alert.metric')
                        }
                    />
                )}
            />

            <Flex flexGrow={1} direction="column" sx={{ mt: 2 }}>
                <Typography variant="caption">
                    {t('alert.create.sentence.1')}
                </Typography>
                <Flex alignItems="start" sx={{ mt: 1 }}>
                    <ToggleButtonGroup
                        color="primary"
                        value={operation}
                        size="small"
                        exclusive
                        sx={{ mr: 2 }}
                        onChange={(e, value) => onOperationChange(value)}
                    >
                        <ToggleButton value="lower">
                            {t('alert.create.lower')}
                        </ToggleButton>
                        <ToggleButton value="greater">
                            {t('alert.create.greater')}
                        </ToggleButton>
                    </ToggleButtonGroup>
                    <Typography variant="caption" sx={{ mt: 1 }}>
                        {t('alert.create.sentence.2')}
                    </Typography>
                    <TextField
                        sx={{ ml: 2, flex: 1 }}
                        size="small"
                        label={t('alert.threshold')}
                        value={threshold}
                        onChange={onThresholdChange}
                        type="text"
                        error={!isThresholdValid(threshold)}
                        helperText={
                            !isThresholdValid(threshold) &&
                            t('alert.invalidThreshold')
                        }
                        InputProps={{
                            endAdornment: currentMetric?.type && (
                                <InputAdornment position="end">
                                    {getSymbolForElementType(
                                        currentMetric.type,
                                    )}
                                </InputAdornment>
                            ),
                        }}
                        disabled={isFetching}
                    />
                </Flex>
            </Flex>
            {action.name === 'create' && (
                <Flex align="center" sx={{ gap: 2 }}>
                    <PreviousStepButton />
                    <NextStepButton
                        disabled={isDisabled}
                        className="pendo-alerts-alertthreshold-step"
                    />
                </Flex>
            )}
        </FormControl>
    );
};

export default AlertThresholdStep;
